import {
	type DeliveryMethod,
	InfoIcon,
	Message,
	Tooltip,
} from "@au-schein-de/au-ui-library";
import type { ProductNoEdibleInfo } from "@dransay/api";
import type { ProductVendorListItem } from "@dransay/api/models";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLoaderData } from "react-router";
import { DismissibleBanner } from "~/components/dismissible-banner.component";
import { useVendorSelection } from "~/contexts/vendor-selection.context";
import { useVendorSelectionPriceComparison } from "~/features/price-comparison/use-vendor-selection-price.comparison";
import {
	type ProductDetailsSelectVendorItem,
	ProductDetailsVendorSelect,
} from "~/features/products/product-details-vendor-select.component";
import { useMarket } from "~/hooks/use-market";
import type { loader } from "~/routes/_storefront._products.product.$slug.$productId";
import type { MarketConfig } from "~/tooling/market/config";
import { formatAddress } from "./filters/utils/format-address";
import { ProductDetailsAddToCart } from "./product-details-add-to-cart.component";

type ProductDetailsPricingProps = {
	product: ProductNoEdibleInfo;
	localVendorId: string | undefined;
	onLocalVendorChange: (vendorId: string) => void;
};

const vendorToVendorSelectItem = (
	vendor: ProductVendorListItem,
	market: MarketConfig,
): ProductDetailsSelectVendorItem => ({
	...vendor,
	title: vendor.name ?? "",
	value: vendor.id?.toString(),
	addressData: vendor.address,
	distanceMeters: vendor.distanceMeters,
	address: vendor.address ? formatAddress(vendor.address) : undefined,
	deliveryMethodsData: vendor.deliveryMethods,
	deliveryMethods:
		vendor.deliveryMethods
			?.filter((d) => d.enabled)
			.map((d) => d.kind as DeliveryMethod) ?? [],
	price: vendor.price ?? Number.NaN,
	showDeliveryMethodBadgesThreshold: market.showDeliveryMethodBadgesThreshold,
	featured: vendor.featured ?? false,
});

export function ProductDetailsPricing({
	product,
	localVendorId,
	onLocalVendorChange,
}: ProductDetailsPricingProps) {
	const { allVendors } = useLoaderData<typeof loader>();

	const { selectedVendorId, selectedDeliveryMethodKind } = useVendorSelection();
	const market = useMarket();

	const { selectedVendor, vendorOptions, addToCartSelectedVendor } =
		useMemo(() => {
			const { selected, selectedForCart, others } = (allVendors ?? []).reduce(
				(acc, vendor) => {
					if (vendor?.id.toString() === localVendorId) {
						acc.selected.push(vendorToVendorSelectItem(vendor, market));
						acc.selectedForCart.push({
							...vendor,
							id: vendor.id.toString(),
						});
						return acc;
					}
					acc.others.push(vendorToVendorSelectItem(vendor, market));
					return acc;
				},
				{ selected: [], selectedForCart: [], others: [] } as {
					selected: ProductDetailsSelectVendorItem[];
					selectedForCart: ProductVendorListItem[];
					others: ProductDetailsSelectVendorItem[];
				},
			);
			return {
				selectedVendor: selected[0],
				addToCartSelectedVendor: selectedForCart[0],
				vendorOptions: [selected[0], ...others].filter(Boolean),
			};
		}, [allVendors, localVendorId, market]);

	const { t } = useTranslation(["product", "common"]);
	const { getPriceComparisonDiscount } = useVendorSelectionPriceComparison({
		vendorOptions,
	});

	const isOutOfStock = product.isOutOfStock;
	const hasOffers = Boolean(selectedVendor);
	const priceComparisonDiscount = getPriceComparisonDiscount(selectedVendor);

	return (
		<>
			{!isOutOfStock && !hasOffers && (
				<div className="flex flex-col w-full gap-3">
					<div className="flex flex-row justify-between items-center px-1">
						<div className="flex gap-1 items-center">
							<span
								className="text-body-bold text-gray-700"
								data-testid="buying-from-label"
							>
								{t("common:buyingFrom")}
							</span>
							<span className="inline-flex text-gray-500">
								<Tooltip.Info
									info={t("common:buyingFromInfo")}
									iconClassName="text-ansay-700"
								/>
							</span>
						</div>
						<span
							className="text-body-sm text-gray-500"
							data-testid="no-offers-count"
						>
							{t("vendor.pdp.noOffers.count")}
						</span>
					</div>
					<output data-testid="product-details-no-offers-message">
						<Message
							variant="info"
							title={t("vendor.pdp.noOffers.title", {
								deliveryMethod: t(
									`deliveryMethods.${selectedDeliveryMethodKind ?? "shipping"}`,
								),
							})}
							icon={<InfoIcon.Filled size={5} colorToken="info-700" />}
							className="w-full"
						>
							{t("vendor.pdp.noOffers.description")}
						</Message>
					</output>
				</div>
			)}

			{!isOutOfStock && hasOffers && (
				<div className="flex flex-col w-full">
					<div className="flex w-full space-between gap-2 mb-4">
						{selectedVendor && (
							<ProductDetailsVendorSelect
								selectedVendor={selectedVendor}
								vendorOptions={vendorOptions}
								selectedVendorId={selectedVendorId}
								product={product}
								onVendorSelection={(vendor) =>
									onLocalVendorChange(vendor.id.toString())
								}
							/>
						)}
					</div>
					{priceComparisonDiscount.visible && (
						<DismissibleBanner
							className="mb-4"
							message={t("vendor.pdp.discountInfoMessage")}
							variant="warning"
							icon={<InfoIcon size={4} colorToken="warning-400" />}
							testId="pdp-discount-info"
						/>
					)}
				</div>
			)}

			{(hasOffers || isOutOfStock) && (
				<ProductDetailsAddToCart
					product={product}
					vendor={addToCartSelectedVendor}
					priceComparisonDiscount={priceComparisonDiscount}
				/>
			)}
		</>
	);
}
